import { Card, Collapse, Empty, Statistic, Typography } from "antd";
import type { CollapseProps } from "antd";
import { useMemo } from "react";
import dayjs from "dayjs";
import Visual from "../../controls/Visual";

export interface UserMetricsPanelProps {
  readonly history: { [key: string]: number };
  readonly loyaltyPoints?: number | null;
  readonly lastLoginAt?: string | Date | null;
  readonly createdOn?: string | Date | null;
}

const WINDOW_DAYS = 90;

/** The shared metrics accordion: a 90-day login-activity chart + placeholder panels. */
function UserMetricsPanel(props: UserMetricsPanelProps) {
  // P6: fill the full 90-day grid with zeros for days without logins. The hook only hands us the days
  // that have at least one login, so without this the line jumps across missing dates and looks like
  // the user was more active than they were.
  const activityData = useMemo(() => {
    const result: { day: string; logins: number }[] = [];
    for (let i = WINDOW_DAYS - 1; i >= 0; i--) {
      const day = dayjs().subtract(i, "days").format("YYYY-MM-DD");
      result.push({ day, logins: props.history[day] ?? 0 });
    }
    return result;
  }, [props.history]);

  // P2: empty-state placeholder when there's nothing to plot. Previously the chart rendered an empty
  // canvas and looked broken; now the operator sees why it's blank.
  const hasAnyLogin = activityData.some((d) => d.logins > 0);

  const totalLogins = activityData.reduce((sum, d) => sum + d.logins, 0);
  const activeDays = activityData.filter((d) => d.logins > 0).length;
  const avgPerActiveDay = activeDays > 0 ? totalLogins / activeDays : 0;
  const daysSinceLastLogin = props.lastLoginAt
    ? dayjs().diff(dayjs(props.lastLoginAt), "day")
    : null;
  const daysSinceJoined = props.createdOn
    ? dayjs().diff(dayjs(props.createdOn), "day")
    : null;

  const items: CollapseProps["items"] = [
    {
      key: "logins",
      label: "Login Activity",
      children: (
        <Card>
          {hasAnyLogin ? (
            <Visual
              data={activityData}
              width="auto"
              render={(chart, data) => {
                chart
                  .data(data)
                  .encode("x", "day")
                  .encode("y", "logins")
                  // P1: dropped the misconfigured `scale("x", {range: [0,1], min: 0, max: 100})` — that
                  // was treating ISO date strings as if they were a 0-100 numeric scale. G2 picks a sane
                  // band scale for strings on its own.
                  .scale("y", { domainMin: 0, nice: true })
                  .axis("y", { title: "Logins" })
                  // P3: show DD/MM instead of the full ISO string — fits the axis without rotating.
                  .axis("x", {
                    title: "Date",
                    labelFormatter: (value: string) => dayjs(value).format("DD/MM"),
                  })
                  .line();
                // P4: default tooltip is useful here (shows date + login count on hover). Previous code
                // explicitly killed it with `.tooltip(false)`, which hid the one piece of per-day detail
                // the chart was supposed to surface.
                chart.point().style("fill", "white");
              }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No login activity in the last 90 days"
            />
          )}
        </Card>
      ),
    },
    {
      key: "metrics",
      label: "Performance Metrics",
      children: (
        <Card>
          <div className="grid grid-cols-3 gap-4">
            <Statistic title="Total Logins (90d)" value={totalLogins} />
            <Statistic title="Active Days (90d)" value={activeDays} suffix={`/ ${WINDOW_DAYS}`} />
            <Statistic
              title="Avg Logins / Active Day"
              value={avgPerActiveDay}
              precision={avgPerActiveDay % 1 === 0 ? 0 : 2}
            />
          </div>
          {!hasAnyLogin && (
            <Typography.Text type="secondary" className="mt-3 block text-center">
              No activity recorded in the last {WINDOW_DAYS} days.
            </Typography.Text>
          )}
        </Card>
      ),
    },
    {
      key: "engagement",
      label: "Engagement",
      children: (
        <Card>
          <div className="grid grid-cols-3 gap-4">
            <Statistic
              title="Loyalty Points"
              value={props.loyaltyPoints ?? 0}
            />
            <Statistic
              title="Last Login"
              value={
                daysSinceLastLogin === null
                  ? "—"
                  : daysSinceLastLogin === 0
                    ? "Today"
                    : `${daysSinceLastLogin}d ago`
              }
            />
            <Statistic
              title="Member Since"
              value={
                daysSinceJoined === null
                  ? "—"
                  : props.createdOn
                    ? dayjs(props.createdOn).format("DD/MM/YYYY")
                    : "—"
              }
              suffix={
                daysSinceJoined !== null ? `(${daysSinceJoined}d)` : undefined
              }
            />
          </div>
        </Card>
      ),
    },
  ];

  return <Collapse accordion destroyInactivePanel items={items} />;
}

export default UserMetricsPanel;
